import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { baseURl } from "../../../utils/urls";
import useToast from "../../../hooks/userToast";
import Sidebar from "../../Sidebar";
import DoctorHeader from "../../../doctor/DoctorHeader";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Upload,
  message,
} from "antd";
import SignatureCrop from "../login/SignatureCrop/SignatureCrop";
import { getRoleName } from "../../../utils/roles";
import { useNavigate, useParams } from "react-router-dom";
import { useGet } from "../../../hooks/apiHooks";
import useUser from "../../../hooks/useUser";
import { UploadOutlined } from "@ant-design/icons";

const { Option } = Select;

const UserDetails = () => {
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const { id } = useParams();
  const { toastSuccess, toastError } = useToast();
  const { userToken, isSubAdmin } = useUser();
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [finalImage, setFinalImage] = useState();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const navigate = useNavigate();
  const { data: userData, loading, refetch } = useGet(`/users/${id}`);
  const checkSubAdmin = userData?.user["role.name"] === "subadmin";
  const [signature, setSignature] = useState();

  const [form] = Form.useForm();
  const selectedRole = Form.useWatch("role", form);

  useEffect(() => {
    fetchRoles();
    fetchCategories();
    refetch();
  }, []);
  useEffect(() => {
    if (!loading && userData?.user) {
      const user = userData.user;
      form.setFieldsValue({
        firstName: user.name.split(" ")[0],
        lastName: user.name.split(" ").slice(1).join(" "),
        email: user.email,
        phone: user.contact_no,
        address: user.address,
        categoryId:
          categories?.find((category) => category.id === user.category_id)
            ?.id || "",
        role: roles?.find((role) => role.id === user.role_id)?.name || "",
        filename: user.file_name,
        gender: user.gender,
        age: user.age,
        certificatenumber: user?.licence_no,
        digitalSignature: [],
      });
    }
  }, [loading, userData, roles, categories]);

  function dataURLToFile(dataURL, filename) {
    const [header, base64Data] = dataURL.split(",");
    const mime = header.match(/:(.*?);/)[1];
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new File([bytes], filename, {
      type: mime,
      lastModified: Date.now(),
    });
  }

  const onFinish = async (data) => {
    setDisabled(true);
    console.log({ data });
    const formData = new FormData();
    formData.append("name", `${data.firstName} ${data.lastName}`);
    formData.append("contact_no", data.phone);
    formData.append("address", data.address);
    formData.append("email", data.email);
    formData.append("categoryId", checkSubAdmin ? "0" : data.categoryId);
    formData.append("roleName", data.role);
    formData.append("age", data.age);
    formData.append("gender", data.gender);
    if (data?.header?.file) {
      formData.append("site_report_header", data?.header?.file);
    }
    if (data?.footer?.file) {
      formData.append("site_report_footer", data?.footer?.file);
    }

    if (data.role === "radiologist") {
      if (!finalImage && !userData?.user?.file_path) {
        return form.setFields([
          {
            name: "digitalSignature",
            errors: ["Digital signature is required"],
          },
        ]);
      }
      const signature =
        finalImage && dataURLToFile(finalImage, "signature.jpg");
      if (data.certificatenumber?.trim()) {
        formData.append("licence_no", data.certificatenumber);
      }
      formData.append(
        "file_name",
        data.filename || data.digitalSignature[0]?.name
      );
      signature &&
        formData.append("image", signature || userData?.user?.file_path);
    }

    try {
      await axios.patch(`${baseURl}/users/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toastSuccess("Updated Successfully!");
      navigate("/manageusers");
    } catch (error) {
      toastError(error.response?.data?.message || error.response?.data?.error);
    } finally {
      setDisabled(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${baseURl}/auth/roleList`);
      setRoles(response?.data?.roles?.rows);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${baseURl}/category`, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      setCategories(response?.data?.category?.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    form.setFieldsValue({ digitalSignature: [] });
  };

  const handleOk = () => setOpen(false);
  const handleImage = (signatureCroppedImage) =>
    setFinalImage(signatureCroppedImage);
  const handlePreview = () => setPreviewOpen(false);
  const handleResetFields = () => form.resetFields();
  const imageUploadPropsHeader = {
    name: "file",
    beforeUpload: (file) => {
      return validateImageDimensions(file, 800, 150, "Footer")
        .then(() => false)
        .catch(() => Upload.LIST_IGNORE);
    },
    headers: {
      authorization: "authorization-text",
    },
    accept: ".jpg,.jpeg,.png",
    maxCount: 1,
  };

  const imageUploadPropsFooter = {
    name: "file",
    beforeUpload: (file) => {
      return validateImageDimensions(file, 800, 150, "Footer")
        .then(() => false)
        .catch(() => Upload.LIST_IGNORE);
    },
    headers: {
      authorization: "authorization-text",
    },
    accept: ".jpg,.jpeg,.png",
    maxCount: 1,
  };

  const validateImageDimensions = (file, maxWidth, maxHeight, label) => {
    return new Promise((resolve, reject) => {
      const isImage =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/jpg";

      if (!isImage) {
        message.error("Only .jpg, .jpeg or .png files are allowed!");
        return reject(new Error("Invalid file type")); // ❗ Use Error to fully reject
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > maxWidth || img.height > maxHeight) {
            message.error(
              `${label} image too large. Max allowed is ${maxWidth}px wide x ${maxHeight}px high.`
            );
            reject(new Error("Image dimensions too large")); // ❗ Use Error here too
          } else {
            resolve(false); // false = do not auto-upload
          }
        };
        img.onerror = () => {
          message.error("Invalid image file.");
          reject(new Error("Could not read image")); // ❗ Reject with error
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };
  return (
    <div>
      <DoctorHeader />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {!loading && (
            <div className="main-wrapper login-body manage_useredit">
              <div className="container-fluid px-0">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="login-wrapper">
                      <div className="loginbox">
                        <div className="login-right">
                          <div className="login-right-wrap">
                            <h2>Edit User</h2>
                            <Form
                              form={form}
                              layout="vertical"
                              onFinish={onFinish}
                              className="row"
                            >
                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="First Name"
                                  name="firstName"
                                  rules={[
                                    {
                                      required: true,
                                      message: "First name required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </div>
                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Last Name"
                                  name="lastName"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Last name required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </div>

                              {!isSubAdmin && (
                                <div className="col-lg-6 col-12">
                                  <Form.Item
                                    label="Category"
                                    name="categoryId"
                                    rules={[
                                      {
                                        required: true,
                                        message: "Category required",
                                      },
                                    ]}
                                  >
                                    <Select placeholder="Select Category">
                                      {categories?.map((el) => (
                                        <Option key={el.id} value={el.id}>
                                          {el.name}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </div>
                              )}

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Role"
                                  name="role"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Role required",
                                    },
                                  ]}
                                >
                                  <Select
                                    disabled={checkSubAdmin}
                                    placeholder="Select Role"
                                  >
                                    {roles
                                      ?.filter((el) => el.name !== "superadmin")
                                      ?.map((el) => (
                                        <Option
                                          key={el.id}
                                          value={el.name}
                                          disabled={el.name === "subadmin"}
                                        >
                                          {getRoleName(el.name)}
                                        </Option>
                                      ))}
                                  </Select>
                                </Form.Item>
                              </div>

                              {selectedRole === "radiologist" && (
                                <>
                                  <div className="col-lg-6 col-12">
                                    <Form.Item
                                      label="License/Certificate No."
                                      name="certificatenumber"
                                    >
                                      <Input.TextArea />
                                    </Form.Item>
                                  </div>
                                  <div className="col-lg-6 col-12">
                                    <Form.Item
                                      label="File Name"
                                      name="filename"
                                      rules={[
                                        {
                                          required: true,
                                          message: "File name required",
                                        },
                                      ]}
                                    >
                                      <Input />
                                    </Form.Item>
                                  </div>
                                  <div className="col-lg-6 col-12">
                                    <Form.Item
                                      label="Digital Signature"
                                      name="digitalSignature"
                                      valuePropName="fileList"
                                      rules={[
                                        {
                                          validator: () => {
                                            if (
                                              !finalImage &&
                                              !userData?.user?.file_path
                                            ) {
                                              return Promise.reject(
                                                "Digital signature is required"
                                              );
                                            }
                                            return Promise.resolve();
                                          },
                                        },
                                      ]}
                                    >
                                      <Input
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.bmp,.jfif,.webp"
                                        disabled={
                                          !!form.getFieldValue(
                                            "digitalSignature"
                                          ).length
                                        }
                                        // onClick={() => setOpen(true)}
                                        ref={fileInputRef}
                                        onChange={(e) => {
                                          const file = e.target.files[0];
                                          console.log("file---->", file);
                                          if (file) {
                                            form.setFieldsValue({
                                              digitalSignature: file,
                                            }); // store the actual File
                                            setSignature(file);
                                            setFinalImage(""); // reset previous cropped image
                                            setOpen(true); // open crop modal
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                    {(finalImage ||
                                      userData?.user?.file_path) && (
                                      <Button
                                        style={{ marginTop: 10 }}
                                        onClick={() => setPreviewOpen(true)}
                                      >
                                        Signature Preview
                                      </Button>
                                    )}
                                  </div>
                                </>
                              )}

                              {selectedRole == "siteperson" && (
                                <>
                                  <Form.Item
                                    name="header"
                                    label="Header Image (.jpg/.png)  (MAX width:1000px, height:200px)"
                                  >
                                    <Upload {...imageUploadPropsHeader}>
                                      <Button icon={<UploadOutlined />}>
                                        Upload Header
                                      </Button>
                                    </Upload>
                                  </Form.Item>

                                  <Form.Item
                                    name="footer"
                                    label="Footer Image (.jpg/.png) (MAX width:1000px, height:200px)"
                                  >
                                    <Upload {...imageUploadPropsFooter}>
                                      <Button icon={<UploadOutlined />}>
                                        Upload Footer
                                      </Button>
                                    </Upload>
                                  </Form.Item>
                                </>
                              )}

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Email"
                                  name="email"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Email required",
                                    },
                                    {
                                      type: "email",
                                      message: "Invalid email address",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </div>

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Phone"
                                  name="phone"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Phone required",
                                    },
                                    {
                                      pattern: /^\d{10}$/,
                                      message:
                                        "Phone number must be exactly 10 digits",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </div>

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Gender"
                                  name="gender"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Gender required",
                                    },
                                  ]}
                                >
                                  <Select placeholder="Select Gender">
                                    <Option value="male">Male</Option>
                                    <Option value="female">Female</Option>
                                  </Select>
                                </Form.Item>
                              </div>

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Age"
                                  name="age"
                                  rules={[
                                    { required: true, message: "Age required" },
                                  ]}
                                >
                                  <InputNumber
                                    min={1}
                                    max={99}
                                    className="w-100"
                                  />
                                </Form.Item>
                              </div>

                              <div className="col-lg-6 col-12">
                                <Form.Item
                                  label="Address"
                                  name="address"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Address required",
                                    },
                                  ]}
                                >
                                  <Input />
                                </Form.Item>
                              </div>

                              <div className="form-group login-btn d-flex gap-5 justify-content-center mt-5">
                                <Button
                                  type="primary"
                                  htmlType="submit"
                                  disabled={disabled}
                                  className="w-25"
                                >
                                  Update
                                </Button>
                                <Button
                                  onClick={handleResetFields}
                                  className="w-25"
                                >
                                  Reset
                                </Button>
                              </div>
                            </Form>

                            <Modal
                              title="Edit Signature"
                              open={open}
                              onCancel={handleCancel}
                              onOk={handleOk}
                              okText="Submit"
                            >
                              <SignatureCrop
                                imgSrc={signature}
                                handleImage={handleImage}
                              />
                            </Modal>

                            <Modal
                              title="Signature Preview"
                              open={previewOpen}
                              onCancel={handlePreview}
                              footer={null}
                            >
                              <img
                                src={finalImage || userData?.user?.file_path}
                                style={{ maxWidth: "100%", marginTop: 10 }}
                              />
                            </Modal>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
